import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Star, Clock, MapPin, Shield, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import ServiceCard from '../components/ServiceCard';
import TestimonialCard from '../components/TestimonialCard';
import TrustBar from '../components/TrustBar';
import EmergencyBanner from '../components/EmergencyBanner';
import FAQAccordion from '../components/FAQAccordion';
import { companyInfo, services, whyUs, serviceCities, testimonials, faqs } from '../data/mock';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B2A4A]/5 via-white to-[#2F80ED]/5"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#2F80ED]/10 text-[#2F80ED] px-4 py-2 rounded-full mb-6">
                <Shield className="w-4 h-4" />
                <span className="text-sm font-semibold">Plombier certifié à Lyon</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0B2A4A] mb-6 leading-tight">
                Plombier à Lyon – Dépannage rapide & travail soigné
              </h1>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Fuite, débouchage, chauffe-eau, rénovation : intervention sur Lyon & Grand Lyon. 
                Devis clair. Délais rapides. Travail garanti.
              </p>
              
              {/* Trust Badges */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200 text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span className="font-bold text-[#0B2A4A]">{companyInfo.rating}★</span>
                  </div>
                  <p className="text-xs text-gray-600">{companyInfo.reviewCount} avis</p>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200 text-center">
                  <Clock className="w-5 h-5 text-[#2F80ED] mx-auto mb-1" />
                  <p className="text-xs text-gray-600 font-semibold">Urgences 7j/7</p>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200 text-center">
                  <CheckCircle className="w-5 h-5 text-[#2F80ED] mx-auto mb-1" />
                  <p className="text-xs text-gray-600 font-semibold">Devis gratuit</p>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200 text-center">
                  <MapPin className="w-5 h-5 text-[#2F80ED] mx-auto mb-1" />
                  <p className="text-xs text-gray-600 font-semibold">Rayon {companyInfo.serviceRadius}</p>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <a href={`tel:${companyInfo.phoneFormatted}`} className="flex-1 sm:flex-initial">
                  <Button size="lg" className="w-full bg-[#2F80ED] hover:bg-[#0B2A4A] text-white gap-2 shadow-lg hover:shadow-xl transition-all duration-300">
                    <Phone className="w-5 h-5" />
                    Appeler maintenant
                  </Button>
                </a>
                <Link to="/contact" className="flex-1 sm:flex-initial">
                  <Button size="lg" variant="outline" className="w-full border-[#2F80ED] text-[#2F80ED] hover:bg-[#2F80ED] hover:text-white transition-all duration-300">
                    Demander un devis
                  </Button>
                </Link>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1749532125405-70950966b0e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxwbHVtYmVyfGVufDB8fHx8MTc3MDQwMDEyN3ww&ixlib=rb-4.1.0&q=85"
                  alt="Plombier professionnel AquaTherm"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B2A4A]/20 to-transparent"></div>
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl p-4 shadow-xl border border-gray-200">
                <p className="text-sm text-gray-600 mb-1">Disponible maintenant</p>
                <a href={`tel:${companyInfo.phoneFormatted}`} className="text-2xl font-bold text-[#2F80ED] hover:text-[#0B2A4A] transition-colors">
                  {companyInfo.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar - Nouveau */}
      <TrustBar />

      {/* Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="bg-[#2F80ED] text-white mb-4">Nos services</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B2A4A] mb-4">
              Intervention rapide pour tous vos besoins
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Du dépannage urgent à la rénovation complète, nous gérons tous vos projets de plomberie avec professionnalisme.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {services.map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>

          <div className="text-center">
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-[#2F80ED] text-[#2F80ED] hover:bg-[#2F80ED] hover:text-white transition-all duration-300">
                Voir tous nos services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Emergency Banner - Nouveau */}
      <EmergencyBanner />

      {/* Why Us Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B2A4A] mb-4">
              Pourquoi choisir AquaTherm ?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Un service de qualité, transparent et respectueux de vos attentes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyUs.map((item, index) => {
              const iconMap = {
                Clock,
                FileText: CheckCircle,
                Sparkles: Shield,
                MessageCircle: Phone
              };
              const IconComponent = iconMap[item.icon] || CheckCircle;
              
              return (
                <div key={index} className="text-center group">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#2F80ED] to-[#0B2A4A] rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-bold text-lg text-[#0B2A4A] mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Zone Section */}
      <section className="py-16 bg-[#0B2A4A] text-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-[#2F80ED] text-white mb-4">Zone d'intervention</Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Lyon & Grand Lyon (rayon {companyInfo.serviceRadius})
              </h2>
              <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                Nous intervenons rapidement sur Lyon et toutes les communes du Grand Lyon. 
                Dépannage d'urgence 7j/7 dans un rayon de 25 km.
              </p>
              <div className="flex flex-wrap gap-2 mb-8">
                {serviceCities.slice(0, 12).map((city, index) => (
                  <Badge key={index} variant="outline" className="bg-white/10 text-white border-white/30">
                    {city}
                  </Badge>
                ))}
              </div>
              <Link to="/zones-tarifs">
                <Button size="lg" className="bg-[#2F80ED] hover:bg-white hover:text-[#0B2A4A] transition-all duration-300">
                  Voir toutes les zones
                </Button>
              </Link>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="aspect-video bg-gray-700 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="w-16 h-16 text-white mx-auto mb-4" />
                  <p className="text-white font-semibold">Carte de la zone d'intervention</p>
                  <p className="text-sm text-gray-300 mt-2">Lyon & Grand Lyon - 25 km</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-4">
              <Star className="w-6 h-6 fill-yellow-400 text-yellow-400" />
              <span className="text-2xl font-bold text-[#0B2A4A]">
                {companyInfo.rating} / 5
              </span>
              <span className="text-gray-600">({companyInfo.reviewCount} avis)</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B2A4A] mb-4">
              Ils nous font confiance
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              La satisfaction de nos clients est notre priorité. Découvrez leurs témoignages.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {testimonials.slice(0, 3).map((testimonial) => (
              <TestimonialCard key={testimonial.id} testimonial={testimonial} />
            ))}
          </div>

          <div className="text-center">
            <Link to="/avis">
              <Button size="lg" variant="outline" className="border-[#2F80ED] text-[#2F80ED] hover:bg-[#2F80ED] hover:text-white transition-all duration-300">
                Voir tous les avis
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section - Nouveau */}
      <FAQAccordion faqs={faqs} />

      {/* Final CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#0B2A4A] via-[#2F80ED] to-[#0B2A4A] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Besoin d'un plombier aujourd'hui ?
          </h2>
          <p className="text-lg text-gray-100 mb-8 max-w-2xl mx-auto">
            Appelez-nous maintenant pour un dépannage rapide ou demandez un devis gratuit en ligne.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${companyInfo.phoneFormatted}`}>
              <Button size="lg" className="bg-white text-[#0B2A4A] hover:bg-gray-100 gap-2 shadow-xl transition-all duration-300">
                <Phone className="w-5 h-5" />
                {companyInfo.phone}
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#0B2A4A] transition-all duration-300">
                Demander un devis gratuit
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
